import React from 'react';
import Icon from '../../../components/AppIcon';

const OfficeLocations = () => {
  const offices = [
  {
    id: 'london',
    city: 'London',
    country: 'United Kingdom',
    type: 'European Headquarters',
    address: '123 Canary Wharf, Level 15, London E14 5AB, United Kingdom',
    phone: '+44 20 7946 0958',
    email: 'london@prox11market.com',
    hours: 'Monday - Friday: 08:00 - 20:00 GMT\nSaturday: 09:00 - 17:00 GMT\nSunday: Closed',
    lat: '51.5074',
    lng: '-0.1278',
    image: "https://images.unsplash.com/photo-1610411254599-2803aca18a56",
    imageAlt: 'Modern glass office building in Canary Wharf London with reflective facade and blue sky',
    services: ['Player Scouting', 'Club Relations', 'Transfer Negotiations', 'Legal Services']
  },
  {
    id: 'new-york',
    city: 'New York',
    country: 'United States',
    type: 'US Headquarters',
    address: '350 Fifth Avenue, Suite 4200, New York, NY 10118, United States',
    phone: '+1 212 555 0198',
    email: 'newyork@prox11market.com',
    hours: 'Monday - Friday: 09:00 - 18:00 EST\nSaturday: 10:00 - 16:00 EST\nSunday: Closed',
    lat: '40.7484',
    lng: '-73.9857',
    image: "https://images.unsplash.com/photo-1682729359826-18cbd164ed2c",
    imageAlt: 'Iconic New York City skyline with modern office buildings and Empire State Building',
    services: ['Player Representation', 'Contract Negotiations', 'Market Analysis', 'Brand Development']
  }];


  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="MapPin" size={16} className="text-primary" />
            <span className="text-xs md:text-sm font-medium text-primary">Global Presence</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-3 md:mb-4">
            Our Office Locations
          </h2>
          
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
            Visit us at any of our strategically located offices across Europe and the USA. Our teams are ready to provide in-person consultations and comprehensive service presentations.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {offices?.map((office) =>
          <div
            key={office?.id}
            className="group bg-card border border-border rounded-xl overflow-hidden hover:shadow-elevated transition-all duration-300">

              <div className="relative h-48 md:h-56 overflow-hidden">
                <img
                src={office?.image}
                alt={office?.imageAlt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                onError={(e) => {
                  e.target.src = '/assets/images/no_image.png';
                }} />

                <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/90 backdrop-blur-sm rounded-full mb-2">
                    <Icon name="Building2" size={14} className="text-primary-foreground" />
                    <span className="text-xs font-medium text-primary-foreground">{office?.type}</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold font-headline text-white">
                    {office?.city}, {office?.country}
                  </h3>
                </div>
              </div>

              <div className="p-4 md:p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Icon name="MapPin" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm md:text-base text-foreground font-medium mb-1">Address</p>
                    <p className="text-xs md:text-sm text-muted-foreground">{office?.address}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Phone" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">Phone</p>
                      <a href={`tel:${office?.phone}`} className="text-xs md:text-sm text-primary hover:underline">
                        {office?.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="Mail" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">Email</p>
                      <a href={`mailto:${office?.email}`} className="text-xs md:text-sm text-primary hover:underline break-all">
                        {office?.email}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Icon name="Clock" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">Office Hours</p>
                    <p className="text-xs md:text-sm text-muted-foreground whitespace-pre-line">{office?.hours}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <p className="text-sm font-medium text-foreground mb-3">Services Available</p>
                  <div className="grid grid-cols-2 gap-2">
                    {office?.services?.map((service, index) =>
                  <div key={index} className="flex items-center gap-2">
                        <Icon name="CheckCircle2" size={14} className="text-success flex-shrink-0" />
                        <span className="text-xs text-muted-foreground">{service}</span>
                      </div>
                  )}
                  </div>
                </div>

                <div className="pt-4">
                  <div className="w-full h-48 rounded-lg overflow-hidden border border-border">
                    <iframe
                    width="100%"
                    height="100%"
                    loading="lazy"
                    title={`${office?.city} Office Location`}
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps?q=${office?.lat},${office?.lng}&z=14&output=embed`} />

                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default OfficeLocations;